// ProgramAnalysisOverview.js
import React, { useState } from 'react';
import {
  FaArrowUp,
  FaArrowDown,
  FaArrowRight,
  FaInfoCircle,
  FaExclamationTriangle,
  FaSignOutAlt,
  FaTools
} from 'react-icons/fa';
import StudentInflectionPoints from './StudentInflectionPoints';

// NEW: Import the ProgramMaps component
import ProgramMaps from './ProgramMaps';

/*********************************
 * HELPER COMPONENTS
 *********************************/
function YearsFillBar({ value }) {
  const maxYears = 6; // for 100% fill
  const fillPercent = Math.min((value / maxYears) * 100, 100);

  let fillColor = 'bg-green-500';
  if (value >= 4 && value < 5) {
    fillColor = 'bg-orange-400';
  } else if (value >= 5) {
    fillColor = 'bg-red-500';
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold">{value} years</span>
      <div className="relative w-24 h-3 bg-gray-200 rounded-full">
        <div
          className={`absolute top-0 left-0 h-3 rounded-full ${fillColor}`}
          style={{ width: `${fillPercent}%` }}
        />
      </div>
    </div>
  );
}

function YearChange({ diff, lowerIsBetter = false }) {
  const isNegative = diff < 0;
  // For time to degree, a negative diff (fewer years) is good.
  const colorClass = lowerIsBetter
    ? (isNegative ? 'text-green-600' : 'text-red-600')
    : (isNegative ? 'text-red-600' : 'text-green-600');
  const arrowIcon = isNegative ? <FaArrowDown /> : <FaArrowUp />;
  return (
    <div className={`text-sm flex items-center gap-1 mt-1 ${colorClass}`}>
      {arrowIcon}
      <span>{Math.abs(diff)}% from last year</span>
    </div>
  );
}

function CreditRatioDisplay({ ratio, diff }) {
  const isNegative = diff < 0;
  const colorClass = isNegative ? 'text-red-600' : 'text-green-600';
  const arrowIcon = isNegative ? <FaArrowDown /> : <FaArrowUp />;
  return (
    <div>
      <p className="text-sm text-gray-500 flex items-center gap-1">
        Credit Ratio <FaInfoCircle className="text-gray-400" />
      </p>
      <div className="text-sm font-semibold mt-1">{ratio}%</div>
      <div className={`text-sm flex items-center gap-1 mt-1 ${colorClass}`}>
        {arrowIcon}
        <span>{Math.abs(diff)}% from last year</span>
      </div>
    </div>
  );
}

function getRecommendationIcon(type) {
  switch (type) {
    case 'highFail':
      return <FaExclamationTriangle />;
    case 'stopOut':
      return <FaSignOutAlt />;
    case 'preReqEffectiveness':
      return <FaTools />;
    default:
      return <FaInfoCircle />;
  }
}

function getRecommendationIconClasses(type) {
  switch (type) {
    case 'highFail':
      return 'bg-red-100 text-red-600';
    case 'stopOut':
      return 'bg-orange-100 text-orange-600';
    case 'preReqEffectiveness':
      return 'bg-blue-100 text-blue-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

/*********************************
 * MAIN COMPONENT
 *********************************/
const ProgramAnalysisOverview = ({ program, onBack }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');

  if (!program) {
    return (
      <div className="flex-1 p-4 flex justify-center bg-gray-50">
        <div className="shadow-md border rounded-lg min-h-[70vh] w-[80vw] bg-white p-6">
          <h2 className="text-xl font-bold">No program selected.</h2>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Back to Programs
          </button>
        </div>
      </div>
    );
  }

  const {
    programCode,
    programTitle,
    yearsToDegree,
    creditRatio,
    recommendations = [],
    overallDiff,
    avgAllPrograms,
    avgAllProgramsDiff,
    creditRatioDiff
  } = program;

  return (
    <div className="flex-1 p-4 flex justify-center">
      <div className="shadow-md border rounded-lg min-h-[70vh] w-[92vw] bg-white p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {programTitle} ({programCode})
            </h1>
            <p className="text-sm text-gray-500">Program Analysis</p>
          </div>
          <button
            onClick={onBack}
            className="text-sm text-blue-600 border border-blue-600 px-3 py-1 rounded hover:bg-blue-50"
          >
            Back to Programs
          </button>
        </div>

        {/* Sub-tab bar */}
        <div className="flex space-x-6 mt-3 text-sm font-medium text-gray-500 border-b border-gray-200">
          <button
            onClick={() => setActiveSubTab('overview')}
            className={`pb-2 ${
              activeSubTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveSubTab('inflection')}
            className={`pb-2 ${
              activeSubTab === 'inflection'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'hover:text-gray-700'
            }`}
          >
            Student Inflection Points
          </button>
          <button
            onClick={() => setActiveSubTab('maps')}
            className={`pb-2 ${
              activeSubTab === 'maps'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'hover:text-gray-700'
            }`}
          >
            Program Maps
          </button>
        </div>

        {/* OVERVIEW TAB */}
        {activeSubTab === 'overview' && (
          <>
            <h2 className="text-base font-semibold text-gray-800 mt-3 mb-3">
              Time to Completion
            </h2>

            {/* Three summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Card 1: Program overall */}
              <div className="border rounded-lg p-4">
                <p className="text-sm text-gray-500">Program Overall</p>
                <YearsFillBar value={yearsToDegree} />
                <YearChange diff={overallDiff} lowerIsBetter={true} />
              </div>

              {/* Card 2: Average across all programs */}
              <div className="border rounded-lg p-4">
                <p className="text-sm text-gray-500">
                  Average Across All Programs
                </p>
                <YearsFillBar value={avgAllPrograms} />
                <YearChange diff={avgAllProgramsDiff} lowerIsBetter={true} />
              </div>

              {/* Card 3: Credit ratio */}
              <div className="border rounded-lg p-4">
                <CreditRatioDisplay ratio={creditRatio} diff={creditRatioDiff} />
              </div>
            </div>

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <div className="mt-4">
                <h3 className="text-base font-semibold text-gray-700 mb-3">
                  Recommendations
                </h3>
                <div className="space-y-4">
                  {recommendations.map((rec, idx) => {
                    const iconClasses = getRecommendationIconClasses(rec.type);
                    const icon = getRecommendationIcon(rec.type);

                    return (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between"
                      >
                        {/* Left side: icon + text */}
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-10 h-10 flex items-center justify-center rounded-full ${iconClasses}`}
                          >
                            {icon}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800 mb-1">
                              {rec.code} {rec.title}
                            </div>
                            <p className="text-sm text-gray-600">
                              {rec.description}
                            </p>
                          </div>
                        </div>

                        {/* Right side: link */}
                        <div className="mt-2 md:mt-0 md:self-start">
                          <a
                            href="#"
                            className="inline-flex items-center text-blue-600 text-sm font-semibold"
                          >
                            More Details
                            <FaArrowRight className="ml-1 mr-1" />
                            Knowledge Base
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* INFLECTION TAB */}
        {activeSubTab === 'inflection' && (
          <StudentInflectionPoints program={program} />
        )}

        {/* PROGRAM MAPS TAB */}
        {activeSubTab === 'maps' && (
          <ProgramMaps program={program} />
        )}
      </div>
    </div>
  );
};

export default ProgramAnalysisOverview;
